import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Heart, Search, ChevronDown, Gift, Moon, Brain, Monitor, Users, Baby, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const categoryIcons = {
  'sleep-bedtime': Moon,
  'behavior-emotions': Brain,
  'screen-time-technology': Monitor,
  'family-bonding': Users,
  'child-development': Baby,
  'bundles-starter-kits': Package,
};

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const { getCartCount, setIsCartOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const cartCount = getCartCount();
  const dropdownRef = useRef(null);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories`);
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsShopDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setIsShopDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/shop') return location.pathname === '/shop';
    if (path === '/bundles') return location.pathname.includes('bundles');
    return location.pathname === path;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  // Separate bundles from regular categories
  const regularCategories = categories.filter(cat => cat.slug !== 'bundles-starter-kits');
  const bundlesCategory = categories.find(cat => cat.slug === 'bundles-starter-kits');

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm" style={{ borderBottom: '1px solid var(--border)' }} data-testid="navbar">
        <div className="wrap">
          <div className="flex justify-between items-center h-20 relative">
            
            {/* Left Navigation */}
            <div className="hidden md:flex items-center space-x-6 flex-shrink-0">
              {/* Shop Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsShopDropdownOpen(!isShopDropdownOpen)}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-teal-600 ${
                    location.pathname.includes('/shop') || location.pathname.includes('/category') 
                      ? 'text-teal-600' 
                      : 'text-slate-600'
                  }`}
                  data-testid="nav-shop-dropdown"
                >
                  Shop
                  <ChevronDown className={`h-4 w-4 transition-transform ${isShopDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {isShopDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    <Link
                      to="/shop"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                      data-testid="dropdown-all-products"
                    >
                      <Package className="h-4 w-4" />
                      All Products
                    </Link>
                    
                    <div className="my-2 border-t border-slate-100"></div>
                    
                    {regularCategories.map((category) => {
                      const IconComponent = categoryIcons[category.slug] || Package;
                      return (
                        <Link
                          key={category.slug}
                          to={`/category/${category.slug}`}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                          data-testid={`dropdown-${category.slug}`}
                        >
                          <IconComponent className="h-4 w-4" />
                          {category.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Browse by Challenge */}
              <Link
                to="/challenges"
                className={`text-sm font-medium transition-colors hover:text-teal-600 ${
                  location.pathname.startsWith('/challenges') ? 'text-teal-600' : 'text-slate-600'
                }`}
                data-testid="nav-challenges"
              >
                Browse by Challenge
              </Link>

              {/* Free Resources */}
              <Link
                to="/free-resources"
                className={`text-sm font-medium transition-colors hover:text-teal-600 ${
                  location.pathname.startsWith('/free-resources') ? 'text-teal-600' : 'text-slate-600'
                }`}
                data-testid="nav-free-resources"
              >
                Free Resources
              </Link>

              {/* About */}
              <Link
                to="/about"
                className={`text-sm font-medium transition-colors hover:text-teal-600 ${
                  location.pathname === '/about' ? 'text-teal-600' : 'text-slate-600'
                }`}
                data-testid="nav-about"
              >
                About
              </Link>

              {/* Contact */}
              <Link
                to="/contact"
                className={`text-sm font-medium transition-colors hover:text-teal-600 ${
                  location.pathname === '/contact' ? 'text-teal-600' : 'text-slate-600'
                }`}
                data-testid="nav-contact"
              >
                Contact
              </Link>
            </div>

            {/* Mobile menu button (left on mobile) */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" style={{ color: 'var(--text)' }} />
              ) : (
                <Menu className="h-5 w-5" style={{ color: 'var(--text)' }} />
              )}
            </button>

            {/* Centered Logo - Using flex-1 and centering instead of absolute positioning */}
            <div className="flex-1 flex justify-center">
              <Link 
                to="/" 
                className="flex items-center hover:opacity-80 transition-opacity py-2 px-3 z-10"
                data-testid="logo"
                aria-label="GoodHabits Family — Home"
              >
                <img
                  src="/images/logos/logo-horizontal.png"
                  alt="GoodHabits Family"
                  className="h-10 sm:h-12 w-auto"
                  width="220"
                  height="75"
                  loading="eager"
                  decoding="async"
                />
              </Link>
            </div>

            {/* Right Navigation (Icons) */}
            <div className="flex items-center space-x-1 flex-shrink-0">
              {/* Search Button */}
              <button
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                data-testid="search-button"
                aria-label="Search"
              >
                <Search className="h-5 w-5" style={{ color: 'var(--text)' }} />
              </button>

              {/* Wishlist - hidden on mobile */}
              <Link
                to="/wishlist"
                className="hidden sm:block p-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="wishlist-button"
                aria-label="Wishlist"
              >
                <Heart className="h-5 w-5" style={{ color: 'var(--text)' }} />
              </Link>

              {/* Cart Button */}
              <button
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setIsCartOpen(true)}
                data-testid="cart-button"
                aria-label="Cart"
              >
                <ShoppingCart className="h-5 w-5" style={{ color: 'var(--text)' }} />
                {cartCount > 0 && (
                  <span 
                    className="absolute -top-0.5 -right-0.5 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
                    style={{ background: 'var(--brand)' }}
                  >
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar (Expandable) */}
        {isSearchOpen && (
          <div className="bg-white py-4" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="wrap">
              <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search for guides, bundles, and more..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                  autoFocus
                  data-testid="search-input"
                />
                <button
                  type="button"
                  onClick={() => setIsSearchOpen(false)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="wrap py-4 space-y-1">
              {/* All Products */}
              <Link
                to="/shop"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/shop') ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Package className="h-5 w-5" />
                <span className="font-medium">All Products</span>
              </Link>
              
              {/* Category Links */}
              <div className="px-4 py-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Categories</span>
              </div>
              
              {regularCategories.map((category) => {
                const IconComponent = categoryIcons[category.slug] || Package;
                return (
                  <Link
                    key={category.slug}
                    to={`/category/${category.slug}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      location.pathname.includes(category.slug) ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <IconComponent className="h-5 w-5" />
                    <span className="font-medium">{category.name}</span>
                  </Link>
                );
              })}

              <div className="my-2 border-t border-slate-100"></div>
              
              {/* Browse by Challenge */}
              <Link
                to="/challenges"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname.startsWith('/challenges') ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-challenges"
              >
                <Brain className="h-5 w-5" />
                <span className="font-medium">Browse by Challenge</span>
              </Link>

              {/* Free Resources */}
              <Link
                to="/free-resources"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname.startsWith('/free-resources') ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-free-resources"
              >
                <Gift className="h-5 w-5" />
                <span className="font-medium">Free Resources</span>
              </Link>

              {/* About */}
              <Link
                to="/about"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/about') ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-about"
              >
                <Users className="h-5 w-5" />
                <span className="font-medium">About</span>
              </Link>

              {/* Contact */}
              <Link
                to="/contact"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/contact') ? 'bg-teal-50 text-teal-700' : 'text-slate-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-contact"
              >
                <Heart className="h-5 w-5" />
                <span className="font-medium">Contact</span>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
